import React from "react";
import { Link, NavLink } from "react-router-dom";
import Icon from "components/Icon";
import InitialsAvatar from "components/InitialsAvatar";
import OverlaySheet from "components/OverlaySheet";
import { formularioContato } from "config/linksInstitucionais";
import { ehNativo } from "services/plataforma";

/**
 * Um bloco da grade. O fundo do ícone e a linha de apoio carregam o estado: rosa no bloco da tela
 * atual (com "Você está aqui" no lugar da linha), âmbar no Meu cadastro com pendência. A borda do
 * ativo é um `ring-inset`, e não `border`, para o bloco não mudar de tamanho ao acender.
 */
function Bloco({ icone, titulo, apoio, ativo = false, alerta = false }) {
	const corBola = alerta ? "bg-amber-100 text-amber-700" : "bg-primary/10 text-primary";
	return (
		<>
			<span className={`flex h-11 w-11 flex-none items-center justify-center rounded-xl ${corBola}`}>
				<Icon name={icone} filled={ativo} className="h-6 w-6" />
			</span>
			<span className="min-w-0">
				<span className="block text-[0.9375rem] font-bold leading-tight text-dark">{titulo}</span>
				<span
					className={`mt-0.5 block text-xs leading-snug ${
						alerta ? "font-medium text-amber-700" : ativo ? "font-medium text-primary" : "text-muted"
					}`}
				>
					{ativo ? "Você está aqui" : apoio}
				</span>
			</span>
			{alerta && (
				<span aria-hidden="true" className="absolute right-3.5 top-3.5 h-2.5 w-2.5 rounded-full bg-amber-500" />
			)}
		</>
	);
}

const classeBloco = ({ ativo = false, largo = false }) =>
	`relative flex rounded-2xl bg-surface p-3.5 text-left shadow-caixa border-caixa border-border transition-transform duration-150 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
		largo ? "col-span-2 min-h-[4.5rem] flex-row items-center gap-3" : "min-h-[7.25rem] flex-col items-start gap-2.5"
	} ${ativo ? "ring-2 ring-inset ring-primary" : ""}`;

/**
 * O menu da conta com sessão, no app e na web mobile: blocos grandes no lugar da lista vertical. Só
 * a NavBar o monta, dentro do painel `#mobile-menu`, sempre que há token. Sem sessão (visitante na
 * web, ou no app a caminho dos Termos) o sanduíche continua a lista com Entrar/Criar conta.
 * Raciocínio em prompts/menu-do-app-em-blocos.md e prompts/menu-web-mobile-em-blocos.md.
 *
 * Os itens e as regras são os mesmos do sanduíche, decididos pela NavBar e recebidos prontos:
 * Buscar só no modo contratando (`mostrarBusca`), troca de modo só para quem pode alternar, Início só
 * na web (`mostrarInicio`; no app "/" leva ao mesmo painel do bloco Meu painel). Não há bloco
 * Administrativo: ele é só para o computador, e no celular o caminho é o menu do avatar do desktop.
 * O que muda é a forma:
 *
 * - a troca de modo é uma faixa larga acima da grade, dizendo o modo atual e a ação;
 * - Meu cadastro é bloco, e a pendência aparece nele em âmbar — a identidade no topo é só leitura;
 * - Ajuda abre um sheet com a lista (cinco links não cabem num bloco), e "Fale conosco" é bloco;
 * - com número ímpar de blocos o último ocupa a linha inteira, para não sobrar um sozinho.
 *
 * O sheet da Ajuda tem o estado na NavBar (`ajudaAberta`): o Escape do painel precisa saber que há
 * um sheet por cima para não fechar os dois de uma vez.
 */
export default function MenuBlocos({
	userName,
	firstName,
	avatar,
	rotuloPapel,
	destinoCadastro,
	documentoPendente,
	assinaturaPendente,
	trabalhando,
	mostrarInicio,
	mostrarBusca,
	trocaDeModo,
	linksAjuda,
	estaEmPaginaAjuda,
	ajudaAberta,
	onAjuda,
	onNavegar,
	onSair,
}) {
	const cadastroPendente = documentoPendente || assinaturaPendente;
	const apoioCadastro = documentoPendente
		? "Falta enviar o documento"
		: assinaturaPendente
			? "Assinatura pendente"
			: trabalhando
				? "Perfil e assinatura"
				: "Dados da conta";

	// Cada entrada vira um NavLink (o `isActive` dele acende o bloco), salvo a Ajuda, que é botão:
	// abre o sheet em vez de navegar, e acende quando a pessoa está numa das páginas que ela lista.
	// `end` no Início: sem ele o NavLink de "/" casaria com qualquer rota e o bloco ficaria sempre aceso.
	const blocos = [
		...(mostrarInicio ? [{ to: "/", end: true, icone: "home", titulo: "Início", apoio: "Página inicial do site" }] : []),
		{ to: destinoCadastro, icone: "person", titulo: "Meu cadastro", apoio: apoioCadastro, alerta: cadastroPendente },
		{ to: "/dashboard", icone: "monitoring", titulo: "Meu painel", apoio: "Pendências e próximos" },
		{ to: "/agenda", icone: "calendar_month", titulo: "Minha agenda", apoio: "Seus agendamentos" },
		...(mostrarBusca ? [{ to: "/profissional", icone: "search", titulo: "Buscar profissional", apoio: "Por cidade e data" }] : []),
		{ ajuda: true, icone: "help", titulo: "Ajuda", apoio: "Passo a passo, perguntas e termos" },
		{ to: formularioContato.to, icone: "support_agent", titulo: formularioContato.label, apoio: "Formulário de contato", semAtivo: true },	];
	const ultimoLargo = blocos.length % 2 === 1;

	return (
		<div className="flex flex-col gap-3 px-3.5 pt-4">
			<div className="flex items-center gap-3 px-1 pb-1">
				{userName ? (
					<InitialsAvatar name={userName} avatar={avatar} className="h-11 w-11 rounded-full text-base" />
				) : (
					<span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-base font-semibold text-white">
						<Icon name="person" filled className="h-6 w-6" />
					</span>
				)}
				<span className="min-w-0">
					<span className="block truncate text-[1.0625rem] font-bold text-dark">{firstName || "Minha conta"}</span>
					{rotuloPapel && <span className="block text-xs text-muted">{rotuloPapel}</span>}
				</span>
			</div>

			{trocaDeModo && (
				<button
					type="button"
					onClick={trocaDeModo.onClick}
					className="flex w-full items-center gap-3 rounded-2xl bg-primary/10 p-3.5 text-left transition-transform duration-150 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
				>
					<span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-primary text-white">
						<Icon name="swap_horiz" className="h-6 w-6" />
					</span>
					<span className="min-w-0">
						<span className="block text-xs font-medium text-primaryHover">{trocaDeModo.modoAtual}</span>
						<span className="block text-[0.9375rem] font-bold text-dark">{trocaDeModo.rotulo}</span>
					</span>
					{trocaDeModo.aviso}
					<Icon name="chevron_right" className={`h-5 w-5 flex-shrink-0 text-primary ${trocaDeModo.aviso ? "" : "ml-auto"}`} />
				</button>
			)}

			<nav aria-label="Navegação principal" className="grid grid-cols-2 gap-2.5">
				{blocos.map((bloco, i) => {
					const largo = ultimoLargo && i === blocos.length - 1;
					if (bloco.ajuda) {
						return (
							<button
								key="ajuda"
								type="button"
								onClick={() => onAjuda(true)}
								aria-haspopup="dialog"
								aria-expanded={ajudaAberta}
								className={classeBloco({ ativo: estaEmPaginaAjuda, largo })}
							>
								<Bloco {...bloco} ativo={estaEmPaginaAjuda} />
							</button>
						);
					}
					// "Fale conosco" usa Link: o NavLink casa pelo pathname e acenderia em /quemsomos,
					// que é a página do formulário e já acende a Ajuda — um bloco aceso basta.
					if (bloco.semAtivo) {
						return (
							<Link key={bloco.to} to={bloco.to} onClick={onNavegar} className={classeBloco({ largo })}>
								<Bloco {...bloco} />
							</Link>
						);
					}
					return (
						<NavLink
							key={bloco.to}
							to={bloco.to}
							end={bloco.end}
							onClick={onNavegar}
							className={({ isActive }) => classeBloco({ ativo: isActive, largo })}
						>
							{({ isActive }) => <Bloco {...bloco} ativo={isActive} />}
						</NavLink>
					);
				})}
			</nav>

			{/* Botão de texto, e não bloco: sair é ação de saída, não destino. Não fecha o painel —
			    abre a confirmação por cima, como o "Sair" do sanduíche (ver handleLogout na NavBar).
			    O texto muda com a plataforma: no navegador não há aplicativo de onde sair, e lá o botão
			    repete o título da confirmação que ele abre ("Sair da conta"). */}
			<button
				type="button"
				onClick={onSair}
				className="mt-1 inline-flex items-center gap-2 self-center rounded-xl px-4 py-3 text-[0.9375rem] font-medium text-muted transition-colors duration-200 hover:bg-error/5 hover:text-error"
			>
				<Icon name="logout" className="h-5 w-5 flex-shrink-0" />
				{ehNativo() ? "Sair do aplicativo" : "Sair da conta"}
			</button>

			{/* `soNaWeb` já veio filtrado em `linksAjuda`. Sem "Fale conosco" aqui: ele é bloco. */}
			<OverlaySheet aberto={ajudaAberta} onFechar={() => onAjuda(false)} titulo="Ajuda">
				<nav aria-label="Ajuda e condições" className="-mx-1 -my-2">
					{linksAjuda.map((link) => (
						<NavLink
							key={link.to}
							to={link.to}
							onClick={() => {
								onAjuda(false);
								onNavegar();
							}}
							className={({ isActive }) =>
								`flex items-center gap-3 rounded-xl px-3 py-3.5 text-base transition-colors duration-200 hover:bg-background hover:text-primary ${
									isActive ? "font-medium text-primary" : "text-dark"
								}`
							}
						>
							{link.label}
							<Icon name="chevron_right" className="ml-auto h-5 w-5 flex-shrink-0 text-muted" />
						</NavLink>
					))}
				</nav>
			</OverlaySheet>
		</div>
	);
}
